import { type Component } from "vue";

import { UNASSIGNED_ASSIGNEE_LABEL, UNASSIGNED_PSEUDO_ASSIGNEE_KEY } from "~/components/visualization/constants";
import { PRIORITY_COLOR_MAP, PRIORITY_OPTIONS, UNPRIORITIZED_PRIORITY_LABEL } from "~/constants/priority";
import {
  CHECKED_PSEUDO_VALUE,
  NULL_PSEUDO_VALUE,
  UNCHECKED_PSEUDO_VALUE,
  UNPRIORITIZED_PSEUDO_VALUE,
  UNSET_PSEUDO_DUID,
} from "~/constants/pseudoDuids";
import {
  EXPONENTIAL_SIZE_COLOR_MAP,
  EXPONENTIAL_SIZES,
  FIBONACCI_SIZE_COLOR_MAP,
  FIBONACCI_SIZES,
  FREE_ENTRY_LIMIT_LABEL,
  FREE_ENTRY_SIZES,
  LINEAR_SIZE_COLOR_MAP,
  LINEAR_SIZES,
  TSHIRT_SIZE_COLOR_MAP,
  TSHIRT_SIZE_TO_NUMBER_SIZE,
  TSHIRT_SIZE_TO_PRETTY_LABEL,
  TSHIRT_SIZES_INCLUDING_NULL,
  UNSIZED_SIZE_LABEL,
} from "~/constants/size";
import { DEFAULT_BLOCK_COLOR, PRIMARY_COLOR } from "~/constants/style";
import {
  CheckedIcon,
  PriorityFieldIcon,
  PriorityIcon,
  SelectFieldIcon,
  SizeIcon,
  UnassignedIcon,
  UncheckedIcon,
  UngroupedIcon,
} from "~/icons";
import { IconSize, LayoutKind, PageKind, PropertyKind, SizeFormat, VisualizationItemType } from "~/shared/enums";
import type {
  GroupByDefinition,
  GroupByGroup,
  Property,
  PropertyConfig,
  PropertyConfigGroupBy,
  VisualizationItem,
  VisualizationItemGroup,
  VisualizationItemOther,
} from "~/shared/types";
import type { useAppStore } from "~/stores";
import type { DataStore } from "~/stores/DataStore";
import { getPageDisplayName } from "~/utils/common";
import { makeDartboardComparator } from "~/utils/comparator";
import { getUserDisplayName, isAgentUser } from "~/utils/user";
// Initialize
type AppStore = ReturnType<typeof useAppStore>;

let appStore: AppStore;
let dataStore: DataStore;

let Avatar: Component;
let PageIcon: Component;
let StatusIcon: Component;

export const init = (newDataStore: DataStore, avatar: Component, pageIcon: Component, statusIcon: Component) => {
  dataStore = newDataStore;
  appStore = dataStore.$useAppStore();

  Avatar = avatar;
  PageIcon = pageIcon;
  StatusIcon = statusIcon;
};

export const UNGROUPED_PSEUDO_GROUP_BY = UNSET_PSEUDO_DUID;
export const UNGROUPED_GROUP_LABEL = "None";
export const NO_VALUE_GROUP_BY_KEY = "@null";
export const UNSIZED_SIZE_GROUP_BY_KEY = "@unsized";

export const UNSET_PSEUDO_SUBGROUP_BY = UNSET_PSEUDO_DUID;
export const DEFAULT_SUBGROUP_LABEL = "None";
export const DEFAULT_SUBGROUP_ID = "@default";
export const DEFAULT_SUBGROUP_COLOR = DEFAULT_BLOCK_COLOR;

export const isGroupHeaderVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemGroup<VisualizationItemType.GROUP_HEADER> =>
  row.itemType === VisualizationItemType.GROUP_HEADER;
export const isCollapsedGroupsVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemOther<VisualizationItemType.COLLAPSED_GROUPS> =>
  row.itemType === VisualizationItemType.COLLAPSED_GROUPS;
export const isCreateTaskVisualizationItem = (
  row: VisualizationItem
): row is
  | VisualizationItemOther<VisualizationItemType.NEW_TASK>
  | VisualizationItemGroup<VisualizationItemType.GROUP_NEW_TASK> =>
  row.itemType === VisualizationItemType.NEW_TASK || row.itemType === VisualizationItemType.GROUP_NEW_TASK;
export const isGroupNewTaskVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemGroup<VisualizationItemType.GROUP_NEW_TASK> =>
  row.itemType === VisualizationItemType.GROUP_NEW_TASK;
export const isAddGroupVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemOther<VisualizationItemType.ADD_GROUP> => row.itemType === VisualizationItemType.ADD_GROUP;
export const isMarginVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemOther<VisualizationItemType.MARGIN> => row.itemType === VisualizationItemType.MARGIN;
export const isLoadingVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemGroup<VisualizationItemType.LOADING> => row.itemType === VisualizationItemType.LOADING;
export const isFillerVisualizationItem = (
  row: VisualizationItem
): row is VisualizationItemOther | VisualizationItemGroup =>
  isCreateTaskVisualizationItem(row) || isMarginVisualizationItem(row) || isLoadingVisualizationItem(row);

export const getDefaultListGroupBy = () => UNGROUPED_PSEUDO_GROUP_BY;

export const getDefaultBoardGroupBy = () => dataStore.defaultStatusProperty.duid;

export const getDefaultSwimlaneGroup = (): GroupByGroup => ({
  id: DEFAULT_SUBGROUP_ID,
  title: DEFAULT_SUBGROUP_LABEL,
  value: null,
  colorHex: DEFAULT_SUBGROUP_COLOR,
  icon: UngroupedIcon,
});

export const KIND_GROUP_BY: PropertyConfigGroupBy<PropertyKind.DEFAULT_KIND> = () => ({
  groups: dataStore.taskKindList.map((kind) => ({
    id: kind.duid,
    value: kind.duid,
    title: kind.title,
    colorHex: kind.colorHex,
    icon: PageIcon,
    iconArgs: { page: kind, size: IconSize.S },
  })),
});

export const DARTBOARD_GROUP_BY: PropertyConfigGroupBy<PropertyKind.DEFAULT_DARTBOARD> = () => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  const dartboards = dataStore.getDartboardList({ excludeFinished: !isDashboard });
  const groups = (
    isDashboard
      ? dartboards.sort(makeDartboardComparator(dataStore.getSpaceByDuid, { chronologicalOrder: true }))
      : dartboards
  ).map((dartboard) => ({
    id: dartboard.duid,
    value: dartboard.duid,
    title: getPageDisplayName(dartboard, dataStore.getSpaceByDuid),
    colorHex: dartboard.colorHex,
    icon: PageIcon,
    iconArgs: { page: dartboard, size: IconSize.S },
  }));
  return { groups };
};

export const STATUS_GROUP_BY: PropertyConfigGroupBy<PropertyKind.DEFAULT_STATUS | PropertyKind.STATUS> = (property) => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  return {
    groups: dataStore.getStatusList(property).map((status) => ({
      id: status.duid,
      value: status.duid,
      title: status.title,
      colorHex: status.colorHex,
      icon: StatusIcon,
      iconArgs: { duid: status.duid, class: isDashboard ? "icon-sm" : "icon-lg" },
    })),
  };
};

export const USER_GROUP_BY: PropertyConfigGroupBy<
  | PropertyKind.DEFAULT_ASSIGNEES
  | PropertyKind.DEFAULT_REVIEWERS
  | PropertyKind.DEFAULT_CREATED_BY
  | PropertyKind.DEFAULT_UPDATED_BY
  | PropertyKind.USER
> = () => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;

  const currentSpace =
    appStore.currentPage?.pageKind === PageKind.DARTBOARD
      ? dataStore.getSpaceByDuid(appStore.currentPage.spaceDuid)
      : null;

  const usersInCurrentSpace = currentSpace
    ? dataStore
        .getUserList()
        .filter(
          (user) =>
            currentSpace.accessibleByTeam || currentSpace.accessibleByUserDuids.includes(user.duid) || isAgentUser(user)
        )
    : dataStore.getUserList();

  return {
    groups: [
      {
        id: UNASSIGNED_PSEUDO_ASSIGNEE_KEY,
        value: null,
        title: UNASSIGNED_ASSIGNEE_LABEL,
        colorHex: DEFAULT_BLOCK_COLOR,
        icon: UnassignedIcon,
        iconArgs: {
          class: isDashboard ? "icon-sm text-vlt" : "icon-lg text-vlt",
        },
      },
      ...usersInCurrentSpace.map((user) => ({
        id: user.duid,
        value: user.duid,
        title: getUserDisplayName(user),
        colorHex: user.colorHex,
        icon: Avatar,
        iconArgs: {
          user,
          class: isDashboard ? "icon-sm" : "icon-lg",
        },
      })),
    ],
  };
};

export const PRIORITY_GROUP_BY: PropertyConfigGroupBy<PropertyKind.DEFAULT_PRIORITY> = () => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  return {
    groups: PRIORITY_OPTIONS.map((priority) => {
      const hasPriority = priority !== null;
      const colorHex = hasPriority ? (PRIORITY_COLOR_MAP.get(priority) ?? DEFAULT_BLOCK_COLOR) : DEFAULT_BLOCK_COLOR;
      return {
        id: hasPriority ? priority : UNPRIORITIZED_PSEUDO_VALUE,
        value: priority,
        title: hasPriority ? priority.toString() : UNPRIORITIZED_PRIORITY_LABEL,
        colorHex,
        icon: hasPriority ? PriorityIcon : PriorityFieldIcon,
        iconArgs: { class: isDashboard ? "icon-sm" : "icon-lg", style: { color: colorHex } },
      };
    }),
  };
};

export const SIZE_GROUP_BY: PropertyConfigGroupBy<PropertyKind.DEFAULT_SIZE> = (property) => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;

  const createSizeGroups = (sizes: number[], colorMap: Map<number, string>) => {
    const unsizedGroup = {
      id: UNSIZED_SIZE_GROUP_BY_KEY,
      value: null,
      title: UNSIZED_SIZE_LABEL,
      colorHex: DEFAULT_BLOCK_COLOR,
      icon: SizeIcon,
      iconArgs: {
        format: property.adtl.format,
        class: isDashboard ? "icon-sm" : "icon-lg",
        style: { color: DEFAULT_BLOCK_COLOR },
      },
    };

    return {
      groups: [
        unsizedGroup,
        ...sizes.map((size) => {
          const colorHex = colorMap.get(size) ?? DEFAULT_BLOCK_COLOR;
          return {
            id: size.toString(),
            value: size,
            title: size.toString(),
            colorHex,
            icon: SizeIcon,
            iconArgs: {
              size,
              format: property.adtl.format,
              class: isDashboard ? "icon-sm" : "icon-lg",
              style: { color: colorHex },
            },
          };
        }),
      ],
    };
  };

  if (property.adtl.format === SizeFormat.FIBONACCI) {
    return createSizeGroups(FIBONACCI_SIZES, FIBONACCI_SIZE_COLOR_MAP);
  }

  if (property.adtl.format === SizeFormat.EXPONENTIAL) {
    return createSizeGroups(EXPONENTIAL_SIZES, EXPONENTIAL_SIZE_COLOR_MAP);
  }

  if (property.adtl.format === SizeFormat.LINEAR) {
    return createSizeGroups(LINEAR_SIZES, LINEAR_SIZE_COLOR_MAP);
  }

  if (property.adtl.format === SizeFormat.FREE_ENTRY) {
    const limitGroup = {
      id: FREE_ENTRY_LIMIT_LABEL,
      value: FREE_ENTRY_LIMIT_LABEL,
      title: FREE_ENTRY_LIMIT_LABEL,
      colorHex: DEFAULT_BLOCK_COLOR,
      icon: SizeIcon,
      iconArgs: {
        size: FREE_ENTRY_SIZES[FREE_ENTRY_SIZES.length - 1],
        format: property.adtl.format,
        class: isDashboard ? "icon-sm" : "icon-lg",
        style: { color: DEFAULT_BLOCK_COLOR },
      },
    };
    const numberGroups = createSizeGroups(FREE_ENTRY_SIZES, new Map()).groups;

    return {
      groups: [...numberGroups, limitGroup],
    };
  }

  return {
    groups: TSHIRT_SIZES_INCLUDING_NULL.map((size) => {
      const prettyLabel = size ? TSHIRT_SIZE_TO_PRETTY_LABEL.get(size) : null;
      const numberSize = TSHIRT_SIZE_TO_NUMBER_SIZE.get(size) ?? null;
      const colorHex = size !== null ? (TSHIRT_SIZE_COLOR_MAP.get(size) ?? DEFAULT_BLOCK_COLOR) : DEFAULT_BLOCK_COLOR;
      return {
        id: numberSize?.toString() ?? UNSIZED_SIZE_GROUP_BY_KEY,
        value: numberSize,
        title: (isDashboard ? prettyLabel : size)?.toString() ?? UNSIZED_SIZE_LABEL,
        colorHex,
        icon: SizeIcon,
        iconArgs: {
          class: isDashboard ? "icon-sm" : "icon-lg",
          format: property.adtl.format,
          style: { color: colorHex },
          ...(numberSize !== null ? { size: numberSize } : {}),
        },
      };
    }),
  };
};

export const CHECKBOX_GROUP_BY: PropertyConfigGroupBy<PropertyKind.CHECKBOX> = () => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  return {
    groups: [
      {
        id: UNCHECKED_PSEUDO_VALUE,
        value: false,
        title: "Unchecked",
        colorHex: DEFAULT_BLOCK_COLOR,
        icon: UncheckedIcon,
        iconArgs: { class: isDashboard ? "icon-sm" : "icon-lg", style: { color: DEFAULT_BLOCK_COLOR } },
      },
      {
        id: CHECKED_PSEUDO_VALUE,
        value: true,
        title: "Checked",
        colorHex: PRIMARY_COLOR,
        icon: CheckedIcon,
        iconArgs: { class: isDashboard ? "icon-sm" : "icon-lg", style: { color: PRIMARY_COLOR } },
      },
    ],
  };
};

export const OPTION_GROUP_BY: PropertyConfigGroupBy<
  PropertyKind.DEFAULT_TAGS | PropertyKind.MULTISELECT | PropertyKind.SELECT
> = (property) => {
  const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  return {
    groups: [
      {
        id: NULL_PSEUDO_VALUE,
        value: null,
        title: "(None)",
        colorHex: DEFAULT_BLOCK_COLOR,
        icon: SelectFieldIcon,
        iconArgs: { class: isDashboard ? "icon-sm" : "icon-lg", style: { color: DEFAULT_BLOCK_COLOR } },
      },
      ...dataStore.getOptionList(property).map((option) => ({
        id: option.duid,
        value: option.duid,
        title: option.title,
        colorHex: option.colorHex,
        icon: SelectFieldIcon,
        iconArgs: { class: isDashboard ? "icon-sm" : "icon-lg", style: { color: option.colorHex } },
      })),
    ],
  };
};

export const getGroupByDefinitionList = (propertiesWithConfig: [Property, PropertyConfig][]): GroupByDefinition[] => {
  const options: GroupByDefinition[] = propertiesWithConfig
    .filter(([property, config]) => {
      if (config.groupBy !== null) {
        return true;
      }

      // Show numeric options only in dashboard
      const isDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
      const isNumeric = [PropertyKind.DEFAULT_TIME_TRACKING, PropertyKind.TIME_TRACKING, PropertyKind.NUMBER].includes(
        property.kind
      );
      return isDashboard && isNumeric;
    })
    .map(([property, config]) => ({
      property,
      icon: config.icon,
      isNumeric: !!config.isNumeric,
      ...(config.groupBy ? config.groupBy(property) : { groups: [] }),
    }));

  // Show dartboard option only in views and dashboard
  const dartboardOption = options.find((e) => e.property.kind === PropertyKind.DEFAULT_DARTBOARD);
  if (
    dartboardOption &&
    (!appStore.currentPage || ![PageKind.VIEW, PageKind.DASHBOARD].includes(appStore.currentPage.pageKind))
  ) {
    options.splice(options.indexOf(dartboardOption), 1);
  }

  // Show ungrouped if not in board, or in board when no properties available
  if (appStore.layoutKind !== LayoutKind.BOARD || options.length === 0) {
    options.unshift({
      property: {
        duid: UNGROUPED_PSEUDO_GROUP_BY,
        title: UNGROUPED_GROUP_LABEL,
        hidden: false,
        kind: null,
      } as unknown as Property,
      icon: UngroupedIcon,
      isNumeric: true,
      groups:
        appStore.layoutKind === LayoutKind.BOARD
          ? [
              {
                id: UNGROUPED_PSEUDO_GROUP_BY,
                title: "Ungrouped",
                value: null,
                colorHex: DEFAULT_BLOCK_COLOR,
                icon: UngroupedIcon,
                iconArgs: { class: "icon-md text-lt" },
              },
            ]
          : [],
    });
  }

  return options;
};
